import { useState, useMemo, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams, Link } from 'react-router-dom'
import { plans, projects, PlanItem, Plan, DestinationLicense } from '@/api/client'
import { PlanItemRow } from '@/components/planning/PlanItemRow'
import { ArrowLeft, Loader2, AlertTriangle, Check, RefreshCw, Settings } from 'lucide-react'

export function PlanningPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const qc = useQueryClient()

  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projects.get(projectId!),
    enabled: !!projectId,
  })

  const planQuery = useQuery<Plan>({
    queryKey: ['plan', projectId],
    queryFn: async () => await plans.ensure(projectId!),
    enabled: !!projectId,
  })

  const licensesQuery = useQuery<DestinationLicense[]>({
    queryKey: ['licenses', projectId],
    queryFn: () => plans.destinationLicenses(projectId!),
    enabled: !!projectId,
  })

  const [activeDomain, setActiveDomain] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'selected' | 'unselected'>('all')
  const [search, setSearch] = useState('')
  const [showSettings, setShowSettings] = useState(false)

  const itemsQuery = useQuery<PlanItem[]>({
    queryKey: ['plan-items', projectId, activeDomain, filter],
    queryFn: () => plans.items(projectId!, {
      domain: activeDomain || undefined,
      selectedOnly: filter === 'selected' ? true : undefined,
      take: 500,
    }),
    enabled: !!projectId && !!planQuery.data,
  })

  // Quando plano carrega e ainda não escolheu domínio, escolhe o primeiro
  useEffect(() => {
    if (!activeDomain && planQuery.data?.domains && planQuery.data.domains.length > 0) {
      setActiveDomain(planQuery.data.domains[0])
    }
  }, [planQuery.data, activeDomain])

  // Filtro de busca client-side
  const visibleItems = useMemo(() => {
    let items = itemsQuery.data || []
    if (filter === 'unselected') items = items.filter(i => !i.isSelected)
    if (search.trim()) {
      const q = search.toLowerCase().trim()
      items = items.filter(i =>
        i.sourceUpn.includes(q) ||
        (i.sourceDisplayName?.toLowerCase().includes(q)) ||
        (i.destinationUpn?.toLowerCase().includes(q))
      )
    }
    return items
  }, [itemsQuery.data, filter, search])

  function handleItemUpdated(updated: PlanItem) {
    qc.setQueryData<PlanItem[]>(
      ['plan-items', projectId, activeDomain, filter],
      old => old?.map(i => i.id === updated.id ? updated : i) || [],
    )
    // Refresh plan summary (com debounce implícito via stale)
    qc.invalidateQueries({ queryKey: ['plan', projectId] })
  }

  if (!projectId) return <div>Projeto não encontrado</div>

  if (planQuery.isLoading || projectQuery.isLoading) {
    return (
      <div className="card p-8 text-center text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
        Carregando plano...
      </div>
    )
  }

  if (planQuery.isError) {
    return (
      <div className="card p-6 text-red-700 bg-red-50 border-red-200">
        Erro ao carregar plano: {(planQuery.error as any).message}
      </div>
    )
  }

  const plan = planQuery.data!
  const project = projectQuery.data!
  const licenses = licensesQuery.data || []
  const isApproved = plan.status !== 'Draft'

  return (
    <div>
      <Link to={`/projects/${projectId}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4">
        <ArrowLeft className="h-4 w-4" /> Voltar ao projeto
      </Link>

      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 mb-1">Planejamento</h1>
          <p className="text-sm text-slate-500">
            {project.name} • {project.sourceTenantDisplayName} → {project.destinationTenantDisplayName}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isApproved ? (
            <span className="badge badge-success">{plan.status}</span>
          ) : (
            <Link to={`/projects/${projectId}/review`} className="btn-primary">
              Revisar e aprovar →
            </Link>
          )}
        </div>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <SummaryCard label="Total" value={plan.summary.totalUsers.toString()} />
        <SummaryCard label="Selecionados" value={plan.summary.selectedUsers.toString()} highlight />
        <SummaryCard label="Criar nova" value={plan.summary.createNewCount.toString()} />
        <SummaryCard label="Vincular" value={plan.summary.linkExistingCount.toString()} />
        <SummaryCard
          label="Tempo estimado"
          value={plan.summary.estimatedDurationMinutes > 60
            ? `${Math.floor(plan.summary.estimatedDurationMinutes / 60)}h ${plan.summary.estimatedDurationMinutes % 60}m`
            : `${plan.summary.estimatedDurationMinutes}m`}
        />
      </div>

      {/* Alerta de licenças */}
      {Object.keys(plan.summary.licenseDeficits).length > 0 && (
        <div className="rounded-md bg-orange-50 border border-orange-200 p-3 mb-4 text-sm">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
            <div>
              <strong className="text-orange-900">Licenças insuficientes no destino:</strong>
              <ul className="text-orange-800 mt-1 ml-4 list-disc">
                {Object.values(plan.summary.licenseDeficits).map(d => (
                  <li key={d.skuPartNumber}>
                    <strong>{d.skuPartNumber}</strong>: precisando {d.required}, disponível {d.available} (faltam <strong>{d.missing}</strong>)
                  </li>
                ))}
              </ul>
              <p className="text-xs text-orange-700 mt-1">
                Compre licenças no admin Microsoft 365 e clique "Atualizar" para refrescar.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Settings */}
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => setShowSettings(!showSettings)}
          className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
        >
          <Settings className="h-4 w-4" />
          Configurações do plano {showSettings ? '▼' : '▸'}
        </button>
        <button
          type="button"
          onClick={() => qc.invalidateQueries({ queryKey: ['licenses', projectId] })}
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
        >
          <RefreshCw className="h-3 w-3" /> Atualizar licenças
        </button>
      </div>

      {showSettings && (
        <PlanSettings projectId={projectId} plan={plan} onSaved={() => qc.invalidateQueries({ queryKey: ['plan', projectId] })} />
      )}

      {/* Tabs por domínio */}
      {plan.domains.length > 1 && (
        <div className="border-b border-slate-200 mb-4">
          <div className="flex gap-1 overflow-x-auto">
            {plan.domains.map(d => (
              <button
                type="button"
                key={d}
                onClick={() => setActiveDomain(d)}
                className={`px-4 py-2 text-sm border-b-2 -mb-px whitespace-nowrap ${activeDomain === d ? 'border-blue-600 text-blue-700 font-medium' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
              >
                @{d}
                <span className="ml-2 text-xs text-slate-400">({plan.summary.usersByDomain[d] || 0})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setFilter('all')}
            className={`text-xs px-3 py-1 rounded-md ${filter === 'all' ? 'bg-slate-200 text-slate-900' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            Todos
          </button>
          <button
            type="button"
            onClick={() => setFilter('selected')}
            className={`text-xs px-3 py-1 rounded-md ${filter === 'selected' ? 'bg-slate-200 text-slate-900' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            Selecionados
          </button>
          <button
            type="button"
            onClick={() => setFilter('unselected')}
            className={`text-xs px-3 py-1 rounded-md ${filter === 'unselected' ? 'bg-slate-200 text-slate-900' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            Não selecionados
          </button>
        </div>
        <input
          type="text"
          className="input !py-1 !text-xs !min-h-0 max-w-xs"
          placeholder="Buscar por UPN ou nome..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="text-xs text-slate-500 ml-auto">
          {visibleItems.length} usuário(s) visível(is)
          {planQuery.data?.summary.selectedUsers !== undefined && (
            <> • {planQuery.data.summary.selectedUsers} selecionado(s) total</>
          )}
        </span>
      </div>

      {/* Tabela */}
      <div className="card overflow-x-auto">
        {itemsQuery.isLoading ? (
          <div className="p-8 text-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            Nenhum usuário {search && `corresponde a "${search}"`}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-2 text-left text-xs font-medium text-slate-600 w-10">
                  <BulkSelectControl
                    items={visibleItems}
                    projectId={projectId}
                    onUpdated={() => qc.invalidateQueries({ queryKey: ['plan-items', projectId] })}
                  />
                </th>
                <th className="p-2 text-left text-xs font-medium text-slate-600">Origem</th>
                <th className="p-2 text-left text-xs font-medium text-slate-600">Mailbox / Drive</th>
                <th className="p-2 text-left text-xs font-medium text-slate-600">Destino</th>
                <th className="p-2 text-left text-xs font-medium text-slate-600">UPN destino</th>
                <th className="p-2 text-left text-xs font-medium text-slate-600">Licença</th>
                <th className="p-2 text-left text-xs font-medium text-slate-600" title="Endereço que a conta terá ao fim da migração. Vazio = igual ao UPN de destino.">UPN final</th>
                <th className="p-2 text-left text-xs font-medium text-slate-600">Conteúdo</th>
                <th className="p-2 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {visibleItems.map(item => (
                <PlanItemRow
                  key={item.id}
                  projectId={projectId}
                  item={item}
                  licenses={licenses}
                  defaultDestinationDomain={plan.defaultDestinationDomain}
                  onUpdated={handleItemUpdated}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-xs text-slate-400 mt-4">
        <Check className="h-3 w-3 inline mr-1" />
        Auto-save ativo. Cada alteração é salva automaticamente.
      </p>
    </div>
  )
}

function SummaryCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`card p-3 ${highlight ? 'border-blue-300 bg-blue-50' : ''}`}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-2xl font-semibold ${highlight ? 'text-blue-700' : 'text-slate-900'}`}>{value}</div>
    </div>
  )
}

function BulkSelectControl({ items, projectId, onUpdated }: { items: PlanItem[]; projectId: string; onUpdated: () => void }) {
  const allSelected = items.length > 0 && items.every(i => i.isSelected)
  const [busy, setBusy] = useState(false)

  async function toggle() {
    if (items.length === 0) return
    setBusy(true)
    try {
      await plans.bulkPatch(projectId, {
        itemIds: items.map(i => i.id),
        isSelected: !allSelected,
      })
      onUpdated()
    } finally {
      setBusy(false)
    }
  }

  return (
    <input
      type="checkbox"
      checked={allSelected}
      onChange={toggle}
      disabled={busy || items.length === 0}
      title="Selecionar/desmarcar todos os visíveis"
    />
  )
}

function PlanSettings({ projectId, plan, onSaved }: { projectId: string; plan: Plan; onSaved: () => void }) {
  const [defaultDomain, setDefaultDomain] = useState(plan.defaultDestinationDomain || '')
  const [bins, setBins] = useState(plan.waveBins.join(','))
  const [folderPolicy, setFolderPolicy] = useState(() => {
    try { return JSON.parse(plan.folderPolicyJson || '{}') } catch { return {} }
  })
  const [saving, setSaving] = useState(false)

  async function save() {
    setSaving(true)
    try {
      const binsList = bins.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n) && n > 0)
      await plans.updateDefaults(projectId, {
        defaultDestinationDomain: defaultDomain || null,
        folderPolicyJson: JSON.stringify(folderPolicy),
        waveBins: binsList,
      })
      onSaved()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card p-4 mb-4 space-y-4 bg-slate-50">
      <div>
        <label className="label text-xs">Domínio destino padrão</label>
        <input
          type="text"
          className="input !py-1.5 !text-xs"
          value={defaultDomain}
          onChange={(e) => setDefaultDomain(e.target.value)}
          placeholder="alarav.onmicrosoft.com"
        />
        <p className="text-[11px] text-slate-500 mt-1">
          Usado para gerar UPNs destino. Pode ser sobrescrito por linha.
        </p>
      </div>

      <div>
        <label className="label text-xs">Bins de tamanho de mailbox para ondas (GB)</label>
        <input
          type="text"
          className="input !py-1.5 !text-xs"
          value={bins}
          onChange={(e) => setBins(e.target.value)}
          placeholder="5,10,20,30,40,50"
        />
        <p className="text-[11px] text-slate-500 mt-1">
          Cria 1 onda para cada faixa: &lt;{bins.split(',')[0] || '5'}GB, depois entre os bins, e &gt;{bins.split(',').slice(-1)[0] || '50'}GB.
        </p>
      </div>

      <div>
        <label className="label text-xs">Política de pastas</label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
          {['Inbox', 'SentItems', 'Drafts', 'Archive', 'CustomFolders', 'DeletedItems', 'JunkEmail', 'Outbox'].map(folder => (
            <label key={folder} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!folderPolicy[folder]}
                onChange={(e) => setFolderPolicy({ ...folderPolicy, [folder]: e.target.checked })}
              />
              {folder}
            </label>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="btn-primary !text-xs !py-1.5">
          {saving && <Loader2 className="h-3 w-3 animate-spin" />}
          Salvar configurações
        </button>
      </div>
    </div>
  )
}
